/** @format */
import { css } from 'styled-components';

import { largeTablet } from '@toucan/client/global/application';
import { theme } from '@toucan/client/global/theme';

import { quietText } from '../../../typography';
import { cardHoverLift } from '../../card-hover';
import { KitCardLayout } from './config/constants';
import { StyledKitCardProps } from './config/types';

export const styles = ({ $layout }: StyledKitCardProps) => css`
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
    background-color: var(--colors-surface-primary);
    border-radius: var(--effects-radius-lg);
    box-shadow: var(--effects-shadow-sm);
    ${cardHoverLift}

    /*
     * Full-bleed to the card's own edges — the corner radius on the card
     * clips it, rather than the bar carrying a radius of its own, so the
     * two always match whatever the card's radius happens to be.
     */
    .kit-card-bar {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: var(--layout-spacing-16);
        flex: 0 0 auto;
        padding: var(--layout-spacing-16) var(--layout-spacing-24);
        background-color: var(--colors-brand-secondary);
    }

    .kit-card-heading {
        ${theme.typography.body_large_highlight}
        color: var(--colors-text-inverse);
    }

    .kit-card-stat {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        flex: 0 0 auto;
        text-align: right;
    }

    .kit-card-stat-value {
        ${theme.typography.body_large_highlight}
        font-size: var(--typography-font-size-3xl);
        color: var(--colors-text-inverse);
        white-space: nowrap;
    }

    .kit-card-stat-label {
        ${theme.typography.body_small}
        color: var(--colors-text-inverse);
    }

    .kit-card-body {
        display: flex;
        flex: 1 1 auto;
        padding: var(--layout-spacing-16) var(--layout-spacing-24);
        gap: var(--layout-spacing-16);
    }

    .kit-card-blurb {
        ${quietText}
        color: var(--colors-text-secondary);
    }

    .kit-card-models {
        display: flex;
        flex-direction: column;
        gap: var(--layout-spacing-12);
        list-style: none;
        margin: 0;
        padding: 0;
    }

    ${$layout === KitCardLayout.STACKED &&
    css`
        .kit-card-body {
            flex-direction: column;
        }
    `}

    /*
     * The blurb is given a fixed share of the width rather than sizing to
     * its own text, so a short blurb cannot leave the model rows squeezed
     * into the leftover space.
     */
    ${$layout === KitCardLayout.INLINE &&
    css`
        .kit-card-body {
            flex-direction: row;
            align-items: flex-start;
        }

        .kit-card-blurb {
            flex: 0 0 40%;
        }

        .kit-card-models {
            flex: 1 1 auto;
            min-width: 0;
        }

        /*
         * Reading left to right needs width this card no longer has once the
         * grid it spans has become a single column, so below the same
         * breakpoint the grid gives up on, this card becomes one of the other
         * two: kit under the blurb, at full width. @see kit-section.styles.ts
         */
        ${largeTablet(css`
            .kit-card-body {
                flex-direction: column;
                /*
                 * Stacked children size to their own content unless stretched,
                 * and the kit list needs to span the card for a percentage on
                 * its right edge to land on the card's right edge.
                 */
                align-items: stretch;
            }

            .kit-card-blurb {
                flex: 0 0 auto;
            }
        `)}
    `}
`;
